import { AnyAction, Reducer } from 'redux';
import { Claim, getClaim, getPropertyFromClaim } from '../../../../claim';
import { State } from '../../../../redux/root-reducer';
import { Nullable } from '../../../../shared-types';
import { combineReducersWithDefault } from '../../../../redux/combine-reducers-with-default';

export interface AddressDetails {
    readonly addressLineOne?: string;
    readonly addressLineTwo?: string;
    readonly addressLineThree?: string;
    readonly addressLineFour?: string;
    readonly addressSuburb?: string;
    readonly postalAddressAddressType?: string;
    readonly postalAddressCode?: string;
    readonly postalAddressCountry?: string;
    readonly postalAddressLineOne?: string;
    readonly postalAddressLineThree?: string;
    readonly postalAddressLineTwo?: string;
    readonly postalAddressProvince?: string;
    readonly postalAddressSuburb?: string;
    readonly postalAddressTown?: string;
    readonly sameAsResidentialAddress?: boolean;
    readonly postalPostalCode?: string;
    readonly townOrSuburb?: string;
    readonly addressType?: string;
    readonly country?: string;
    readonly address?: string;
    readonly suburb?: string;
    readonly town?: string;
    readonly postalCode?: string;
    readonly province?: string;
    readonly city?: string;
}

export interface PolicyholderPersonalDetails {
    title?: string;
    fullFirstNames?: string;
    surname?: string;
    cellphoneNumber?: string;
    emailAddress?: string;
    hasMaritalStatusChanged?: string;
    maritalStatus?: string;
    dateOfMarriage?: string;
    dateOfDivorce?: string;
    areYouASouthAfricanResident?: string;
    countryOfResidence?: string;
    proofOfIdentification?: unknown;
    passportNumber?: string;
    hasYourMaritalStatusChangedSinceYouTookUpPolicy?: string;
    idNumber?: string;
}

export interface PolicyholderEmploymentDetails {
    policyholdersOccupation?: string;
    inWhichIndustry?: string;
    nameOfEmployer?: string;
    saIncomeTaxReferenceNumber?: string;
}

export interface CompanyDetails {
    companyTradingName?: string;
    companyRegistrationNumber?: string;
    companyRegistrationDate: Date;
    countryOfIncorporation?: string;
    countryOfCompany?: string;
    howLongHasThePolicyBeenActiveFor?: string;
    phCompanyName?: string;
    phCompanyRegistrationNumber?: string;
    phRegistrationDate: Date;
    phCountryOfIncorporation?: string;
    entityRegistrationNumber?: string;
    dateOfIncorporation: Date;
    entityName?: string;
    entityAddressLine1?: string;
    entityAddressLine2?: string;
    entityProvince?: string;
    entityPostalCode?: string;
    entitySuburb?: string;
    entityTown?: string;
}

export interface PolicyholderIndividual {
    addressDetails?: AddressDetails;
    personalDetails?: PolicyholderPersonalDetails;
    employmentDetails?: PolicyholderEmploymentDetails;
    companyDetails?: CompanyDetails;
}

export interface Policyholder {
    individual?: PolicyholderIndividual;
    isThePolicyHolderAnIndividualOrCompany?: string;
}

export interface LifeAssuredPersonalDetails {
    isTheLifeAssuredTheSameAsPolicyHolder?: string;
    relationshipToPolicyHolder?: string;
    title?: string;
    fullFirstNames?: string;
    surname?: string;
    hasYourMaritalStatusChangedSinceYouTookUpThePolicy?: string;
    maritalStatus?: string;
    areYouASouthAfricanResident?: string;
    passportNumber?: string;
    countryOfResidence?: string;
    lifeAssuredIdDocumentUpload?: unknown;
    idNumber?: string;
    cellphoneNumber?: string;
    emailAddress?: string;
}

export interface MedicalAidDetails {
    doesTheLifeAssuredHaveAMedicalAid?: string;
    medicalAidName?: string;
    dateJoined?: string;
    isLifeAssuredMainMemberOnTheMedicalAid?: string;
    mainMembersTitle?: string;
    otherMedicalAidName?: string;
    medicalAidNumber?: string;
    mainMembersFirstName?: string;
    mainMembersSurname?: string;
    mainMembersIdNumberOrPassportNumber?: string;
}

export interface Occurence {
    dateOfAttendance?: Nullable<Date>;
    reason?: string;
    consultationDate?: Nullable<Date>;
    pleaseProvideDetailsOfTheConsultation?: string;
}

export interface DoctorVisit {
    occurences?: Occurence[];
    addressDetails?: AddressDetails;
    doctorsName?: string;
    doctorsContactNumber?: string;
    doctorName?: string;
    doctorContactNumber?: string;
}

export interface IncomeProtectorClaimConditions {
    doctorVisits?: DoctorVisit[];
    medicalEvidenceOrReports?: unknown[];
    lifeAssuredMedicalReason?: string;
    lifeAssuredDateOfDiagnosis?: Nullable<Date>;
    lifeAssuredMedicalCondition?: string;
    lifeAssuredDiseaseDetails?: string;
    lifeAssuredAccidentDetails?: string;
    policeReport?: unknown;
    whatFormOfTreatmentOrMedicationAreYouCurrentlyTaking?: string;
    whatIsTheMedicalReasonForTheClaimBeingSubmitted?: string;
    pleaseProvideTheDateOfDiagnosisOrDateThatTheEventTookPlace?: Nullable<Date>;
    medicalConditionWasAcquiredDueTo?: string;
    whatWasTheDiseaseDiagnosedFor?: string;
    pleaseProvideDetailsOfTheAccidentIncludingTheCaseNumberAndTheNameOfThePoliceStation?: string;
}

export interface OtherInformation {
    whatIsYourHighestQualification?: string;
    haveYouEverBeenDeclaredInsolventOrAreSequestrationHearingProceedingsPendingOrContemplated?: string;
    doYouConsumeAlcohol?: string;
    howManySpiritsInTotsPerWeek?: string;
    howManyGlassesOfWinePerWeek?: string;
    howManyBeersOrCidersInCanOrBottlePerWeek?: string;
    haveYouEverReceivedAdviceToReduceOrDiscontinueAlcoholConsumption?: string;
    pleaseProvideFullDetailsOfTheAdvice?: string;
    doYouCurrentlyPracticeAnyAvocations?: string;
    pleaseProvideFullDetailsOfTheAvocations?: string;
    pleaseProvideTheAverageNumberOfHoursThatYouWereWorkingPerWeek?: string;
    wereYouPartiallyOrFullyRetiredAtTheDateOfTheClaimEvent?: string;
    retirementFullDetails?: string;
}

export interface LifeAssuredIncomeDetails {
    incomeType?: string;
    proofOfIncomeSubmitted?: string;
    TotalAmountEarned?: string;
    OverheadExpenses?: string;
    employersDeclaration?: string;
}

export interface LifeAssured {
    addressDetails?: AddressDetails;
    PersonalDetails?: LifeAssuredPersonalDetails;
    medicalAidDetails?: MedicalAidDetails;
    incomeProtectorClaimConditions?: IncomeProtectorClaimConditions;
    otherInformation?: OtherInformation;
    incomeDetails?: LifeAssuredIncomeDetails;
}

export interface ClaimantDetails {
    policyNumbers?: string[];
    whoseDetailsAreBeingEntered?: string;
    companyName?: string;
    consultantCode?: string;
    title?: string;
    firstName?: string;
    surname?: string;
    cellphoneNumber?: string;
    telephoneNumber?: string;
    workNumber?: string;
    emailAddress?: string;
    preferredMethodOfCommunication?: string[];
}

export interface Claimant {
    claimantDetails?: ClaimantDetails;
}

export interface SourceOfIncome {
    sourceOfIncome?: string;
    incomeAmount?: string;
    datePaymentReceived?: Nullable<Date>;
}

export interface DetailsOfInabilityToPerformOccupation {
    hasYourMedicalConditionImpactedYourAbilityToPerformYourOccupation?: string;
    willYouBeReturningToWorkFullTimeOrPartTime?: string;
    pleaseProvideDetailsOfTheHoursYouWillBeWorking?: string;
    areYouCurrentlyLosingAnIncomeDueToYourMedicalCondition?: string;
    onWhatDateDidYouStopWorking?: Nullable<Date>;
    whenWillYouBeReturningToWork?: Nullable<Date>;
    pleaseProvideCompleteDetailsOfHowThisConditionHasAffectedYourIncome?: string;
    onWhatDateDidYouLastReceiveAFullIncome?: Nullable<Date>;
    haveYouReceivedAnyIncomeOrAnyBenefitsSinceTheAboveDate?: string;
    sourcesOfIncome?: SourceOfIncome[];
}

export interface BenefitDetail {
    nameOfCompany?: string;
    policyNumber?: string;
    dateOfInception?: Nullable<Date>;
    valueOfLumpSumOrAmountOfMonthlyPayment?: string;
}

export interface OtherBenefits {
    hasOtherBenefits?: string;
    benefitDetails?: BenefitDetail[];
}

export interface OccupationalDisabilityOverheadOne {
    nameOfCurrentOrPreviousEmployer?: string;
    address?: string;
    postalCode?: string;
    dateEmployedFrom?: Nullable<Date>;
    dateEmployedTo?: Nullable<Date>;
    whatWasYourFullTimeOccupationImmediatelyBeforeYourCurrentDisabilityBegan?: string;
    jobDescription?: string;
    isThereAnyHazardAssociatedWithYourOccupation?: string;
    pleaseProvideDetailsOfAnyHazards?: string;
    doYouTravelBeyondTheBordersOfSouthAfricaInTheCourseOfYourDuties?: string;
    administrative?: number;
}

export interface OccupationalDisabilityOverheadTwoItem {
    companyName?: string;
    natureOfOccupation?: string;
    startDate?: Nullable<Date>;
    endDate?: Nullable<Date>;
}

export interface ClaimInformation {
    guaranteedPaymentPeriodClaims?: string[];
    permanentImpairmentClaims?: string[];
    occupationalDisabilityOverheadOne?: OccupationalDisabilityOverheadOne;
    occupationalDisabilityOverheadTwo?: OccupationalDisabilityOverheadTwoItem[];
    detailsOfInabilityToPerformOccupation?: DetailsOfInabilityToPerformOccupation;
    otherBenefits?: OtherBenefits;
}

export interface PaymentInformation {
    paymentBeneficiary?: string;
    accountType?: string;
    doesAccountBelongToAnIndividualOrABusiness?: string;
    accountHolderRelationship?: string;
    isTheAccountHolderASouthAfricanResident?: string;
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
    branchName?: string;
    countryOfcompany?: string;
    companyName?: string;
    companyRegistrationDate?: Nullable<Date>;
    companyRegistrationNumber?: string;
    relationshipToPolicyHolder?: string;
    isTheMonthlyPremiumBeingPaidUsingThisAccount?: string;
}

export interface Payment {
    PaymentInformation?: PaymentInformation;
}

export interface Declaration {
    firstNameAndSurname?: string;
    IdNumber?: string;
    agreedToTermsAndConditions?: boolean;
}

export interface FormDetails {
    policyholder?: Policyholder;
    lifeAssured?: LifeAssured;
    claimant?: Claimant;
    claimInformation?: ClaimInformation;
    payment?: Payment;
    declaration?: Declaration;
}

export const defaultFormDetails: FormDetails = {
    lifeAssured: {},
    declaration: {},
    payment: {},
    policyholder: {},
    claimant: {},
    claimInformation: {},
};

const defaultFormDetailsList: FormDetails[] = [];

export const defaultPolicyholder: Policyholder = {
    individual: {},
};

export const defaultLifeAssured: LifeAssured = {};

export const defaultClaimant: Claimant = {
    claimantDetails: {},
};

export const defaultClaimInformation: ClaimInformation = {};

export const defaultPayment: Payment = {
    PaymentInformation: {},
};

export const defaultDeclaration: Declaration = {};

const policyholderReducer: Reducer<Policyholder | undefined, AnyAction> = (
    state: Policyholder = defaultPolicyholder,
    action: AnyAction
) => {
    switch (action.type) {
        default:
            return state;
    }
};

const lifeAssuredReducer: Reducer<LifeAssured | undefined, AnyAction> = (
    state: LifeAssured = defaultLifeAssured,
    action: AnyAction
) => {
    switch (action.type) {
        default:
            return state;
    }
};

const claimantReducer: Reducer<Claimant | undefined, AnyAction> = (
    state: Claimant = defaultClaimant,
    action: AnyAction
) => {
    switch (action.type) {
        default:
            return state;
    }
};

const claimInformationReducer: Reducer<ClaimInformation | undefined, AnyAction> = (
    state: ClaimInformation = defaultClaimInformation,
    action: AnyAction
) => {
    switch (action.type) {
        default:
            return state;
    }
};

const paymentReducer: Reducer<Payment | undefined, AnyAction> = (
    state: Payment = defaultPayment,
    action: AnyAction
) => {
    switch (action.type) {
        default:
            return state;
    }
};

const declarationReducer: Reducer<Declaration | undefined, AnyAction> = (
    state: Declaration = defaultDeclaration,
    action: AnyAction
) => {
    switch (action.type) {
        default:
            return state;
    }
};

export const formDetailsReducer = combineReducersWithDefault<Nullable<FormDetails>>(
    defaultFormDetails,
    {
        lifeAssured: lifeAssuredReducer,
        declaration: declarationReducer,
        payment: paymentReducer,
        policyholder: policyholderReducer,
        claimant: claimantReducer,
        claimInformation: claimInformationReducer,
    }
);

export const formDetailsListReducer = (
    state: FormDetails[] = [],
    action: AnyAction
): FormDetails[] => {
    switch (action.type) {
        default:
            return state;
    }
};

export {
    policyholderReducer,
    lifeAssuredReducer,
    claimantReducer,
    claimInformationReducer,
    paymentReducer,
    declarationReducer,
};

export function getPolicyholderFromClaim(claim: Claim): Policyholder {
    return getPropertyFromClaim(claim, 'policyholder', defaultPolicyholder);
}

export function getPolicyholder(state: State): Policyholder {
    return getPolicyholderFromClaim(getClaim(state));
}

export function getLifeAssuredFromClaim(claim: Claim): LifeAssured {
    return getPropertyFromClaim(claim, 'lifeAssured', defaultLifeAssured);
}

export function getLifeAssured(state: State): LifeAssured {
    return getLifeAssuredFromClaim(getClaim(state));
}

export function getClaimantFromClaim(claim: Claim): Claimant {
    return getPropertyFromClaim(claim, 'claimant', defaultClaimant);
}

export function getClaimant(state: State): Claimant {
    return getClaimantFromClaim(getClaim(state));
}

export function getClaimInformationFromClaim(claim: Claim): ClaimInformation {
    return getPropertyFromClaim(claim, 'claimInformation', defaultClaimInformation);
}

export function getClaimInformation(state: State): ClaimInformation {
    return getClaimInformationFromClaim(getClaim(state));
}

export function getPaymentFromClaim(claim: Claim): Payment {
    return getPropertyFromClaim(claim, 'Payment', defaultPayment);
}

export function getPayment(state: State): Payment {
    return getPaymentFromClaim(getClaim(state));
}

export function getDeclarationFromClaim(claim: Claim): Declaration {
    return getPropertyFromClaim(claim, 'declaration', defaultDeclaration);
}

export function getDeclaration(state: State): Declaration {
    return getDeclarationFromClaim(getClaim(state));
}

export function getFormDetailsFromClaim(claim: Claim): FormDetails {
    return getPropertyFromClaim(claim, 'formDetails', defaultFormDetails);
}

export function getFormDetails(state: State): FormDetails {
    return getFormDetailsFromClaim(getClaim(state));
}

export function getFormDetailsListFromClaim(claim: Claim): FormDetails[] {
    return getPropertyFromClaim(claim, 'formDetailsList', defaultFormDetailsList);
}

export function getFormDetailsList(state: State): FormDetails[] {
    return getFormDetailsListFromClaim(getClaim(state));
}
